import { GenericChartResponseDTO } from 'submodules/nerit-aquatec-sdk/services/charts/dtos/responses/GenericChartResponseDTO'
import { ConditionalLimitPointTP, ConditionalLimitTP } from 'submodules/nerit-aquatec-sdk/services/charts/types/ConditionalLimitTP'
import { RegulationConditionUtils } from 'modules/fields/utils/RegulationConditionUtils'

/** Cores do gráfico de dois painéis (mockup da Hidrogeo). */
export const CONDITIONAL_CHART_COLORS = {
    parameter: '#24505c',
    limit: '#d98a2b',
    reference: '#7b4bb7',
    inAccordance: '#2fa84f',
    outOfAccordance: '#d0313d',
    bands: ['rgba(120, 200, 210, 0.30)', 'rgba(250, 200, 140, 0.35)', 'rgba(160, 215, 150, 0.35)', 'rgba(200, 160, 220, 0.30)', 'rgba(240, 225, 120, 0.35)', 'rgba(150, 170, 230, 0.30)'],
}

/** Símbolo "X" para não conforme. Registrado em `ChartHistoricalSeriesCP`. */
export const OUT_OF_ACCORDANCE_SYMBOL = 'cross'

type PointKindTP = 'parameter' | 'reference'

/** Dados extras de cada ponto, lidos pelo tooltip. */
export type ConditionalPointCustomTP = {
    kind: PointKindTP
    // Data da coleta (YYYY-MM-DD). O x do ponto é o mês, então não serve para mostrar o dia.
    date?: string
    limit?: number | null
    referenceValue?: number | null
    referenceName?: string
}

/**
 * Opções Highcharts da Série Histórica com limite condicional: um gráfico, dois painéis com eixo X compartilhado.
 * Em cima o parâmetro e o limite tracejado de cada amostra; embaixo o parâmetro de referência sobre as faixas.
 */
export class ConditionalHistoricalChartUtils {

    /** Proporção dos painéis no plot. */
    static readonly PARAMETER_PANE = { top: '0%', height: '58%' }
    static readonly REFERENCE_PANE = { top: '66%', height: '34%' }

    /** Altura do card em Full: dois painéis não cabem nos 350px do gráfico de um painel. */
    static readonly FULL_HEIGHT = 560

    /** Legenda sem colunas alinhadas: com 7 entradas e a fonte maior do arquivo exportado, cabe em duas linhas. */
    static readonly LEGEND = { alignColumns: false }

    static buildSeries(data: GenericChartResponseDTO, toX: (date: string) => number, regulationName?: string): any[] {

        const conditionalLimit = data.conditionalLimit!
        const parameterSerie = data.series[0]
        const referenceName = conditionalLimit.referenceField.name

        // `points` segue a ordem de dataArray da série do parâmetro.
        const parameterPoints = (parameterSerie?.dataArray ?? []).map((item, index) => {
            const point: ConditionalLimitPointTP | undefined = conditionalLimit.points[index]
            const color = ConditionalHistoricalChartUtils.statusColor(point)
            return {
                x: toX(item.x),
                y: +item.y,
                marker: ConditionalHistoricalChartUtils.statusMarker(point),
                dataLabels: color ? { enabled: true, color, style: { textOutline: 'none' } } : { enabled: true },
                custom: { kind: 'parameter', date: item.x, limit: point?.limit ?? null, referenceValue: point?.referenceValue ?? null, referenceName } as ConditionalPointCustomTP,
            }
        })

        const limitPoints = (parameterSerie?.dataArray ?? []).map((item, index) => [toX(item.x), conditionalLimit.points[index]?.limit ?? null])

        const referencePoints = conditionalLimit.referenceSerie.dataArray.map(item => ({
            x: toX(item.x),
            y: +item.y,
            custom: { kind: 'reference', date: item.x, referenceName } as ConditionalPointCustomTP,
        }))

        return [
            {
                name: parameterSerie?.xAxisLabel,
                type: 'line',
                yAxis: 0,
                color: CONDITIONAL_CHART_COLORS.parameter,
                data: parameterPoints,
                dataLabels: {
                    formatter: function (this: any): string {
                        return RegulationConditionUtils.formatNumber(this.y)
                    },
                },
            },
            {
                name: ConditionalHistoricalChartUtils.limitSerieName(conditionalLimit, regulationName),
                type: 'line',
                yAxis: 0,
                color: CONDITIONAL_CHART_COLORS.limit,
                dashStyle: 'Dash',
                data: limitPoints,
                connectNulls: false,
                marker: { enabled: false },
                dataLabels: { enabled: false },
                enableMouseTracking: false,
                custom: { isLimitValue: true },
            },
            {
                name: referenceName,
                type: 'line',
                yAxis: 1,
                color: CONDITIONAL_CHART_COLORS.reference,
                data: referencePoints,
                marker: { enabled: true, symbol: 'circle' },
                dataLabels: {
                    enabled: true,
                    color: CONDITIONAL_CHART_COLORS.reference,
                    style: { textOutline: 'none' },
                    formatter: function (this: any): string {
                        return `${referenceName} ${RegulationConditionUtils.formatNumber(this.y)}`
                    },
                },
            },
            // Legenda das faixas: séries sem dados, só para a legenda (vale também no arquivo exportado).
            ...conditionalLimit.conditions.map((condition, index) => ({
                name: RegulationConditionUtils.legendText(condition, referenceName),
                type: 'area',
                yAxis: 1,
                data: [],
                color: ConditionalHistoricalChartUtils.bandColor(index),
                showInLegend: true,
                enableMouseTracking: false,
                events: { legendItemClick: () => false },
                custom: { isBandLegend: true },
            })),
        ]
    }

    static buildYAxis(data: GenericChartResponseDTO): any[] {

        const conditionalLimit = data.conditionalLimit!
        const extent = ConditionalHistoricalChartUtils.referenceAxisExtent(conditionalLimit)

        const plotBands: any[] = []
        const plotLines: any[] = []
        conditionalLimit.conditions.forEach((condition, index) => {
            const band = RegulationConditionUtils.axisBand(condition, extent)
            if ('value' in band)
                plotLines.push({ value: band.value, color: ConditionalHistoricalChartUtils.bandColor(index), width: 3, zIndex: 1 })
            else
                plotBands.push({ from: band.from, to: band.to, color: ConditionalHistoricalChartUtils.bandColor(index) })
        })

        return [
            {
                ...ConditionalHistoricalChartUtils.PARAMETER_PANE,
                title: { text: data.series[0]?.xAxisLabel },
                min: 0,
                offset: 0,
            },
            {
                ...ConditionalHistoricalChartUtils.REFERENCE_PANE,
                title: { text: conditionalLimit.referenceField.name },
                min: extent.min,
                max: extent.max,
                tickPositions: ConditionalHistoricalChartUtils.referenceTicks(conditionalLimit, extent),
                startOnTick: false,
                endOnTick: false,
                offset: 0,
                plotBands,
                plotLines,
            },
        ]
    }

    /** Eixo do parâmetro de referência: cobre os valores medidos e os valores das faixas, com folga. */
    static referenceAxisExtent(conditionalLimit: ConditionalLimitTP): { min: number, max: number } {

        const values = [
            ...conditionalLimit.referenceSerie.dataArray.map(item => +item.y),
            ...conditionalLimit.conditions.flatMap(condition => [Number(condition.value), ...(condition.valueTo !== undefined && condition.valueTo !== null ? [Number(condition.valueTo)] : [])]),
        ].filter(value => !Number.isNaN(value))

        const min = Math.min(...values)
        const max = Math.max(...values)
        const padding = Math.max((max - min) * 0.1, 0.5)

        return { min: min - padding, max: max + padding }
    }

    /**
     * Ticks do eixo de referência nas fronteiras das faixas, mais os inteiros das pontas.
     * Ex.: pH com faixas em 7,5 / 8,0 / 8,5 → 7; 7,5; 8; 8,5; 9.
     */
    static referenceTicks(conditionalLimit: ConditionalLimitTP, extent: { min: number, max: number }): number[] {

        const boundaries = conditionalLimit.conditions.flatMap(condition => [Number(condition.value), ...(condition.valueTo !== undefined && condition.valueTo !== null ? [Number(condition.valueTo)] : [])])
        const ticks = [Math.ceil(extent.min), ...boundaries, Math.floor(extent.max)]
            .filter(value => !Number.isNaN(value) && value >= extent.min && value <= extent.max)

        return [...new Set(ticks)].sort((a, b) => a - b)
    }

    static limitSerieName(conditionalLimit: ConditionalLimitTP, regulationName?: string): string {
        const regulation = regulationName ? ` ${regulationName}` : ''
        return `Limite${regulation} (conforme ${conditionalLimit.referenceField.name} observado)`
    }

    static bandColor(index: number): string {
        return CONDITIONAL_CHART_COLORS.bands[index % CONDITIONAL_CHART_COLORS.bands.length]
    }

    /** Verde conforme, vermelho não conforme, sem cor própria quando não aplicável. */
    static statusColor(point?: ConditionalLimitPointTP): string | undefined {
        if (!point || point.isOutsidePermittedLimits === null || point.isOutsidePermittedLimits === undefined)
            return undefined
        return point.isOutsidePermittedLimits ? CONDITIONAL_CHART_COLORS.outOfAccordance : CONDITIONAL_CHART_COLORS.inAccordance
    }

    static statusMarker(point?: ConditionalLimitPointTP): any {
        if (!point || point.isOutsidePermittedLimits === null || point.isOutsidePermittedLimits === undefined)
            return { enabled: true }

        if (point.isOutsidePermittedLimits)
            return { enabled: true, symbol: OUT_OF_ACCORDANCE_SYMBOL, radius: 6, lineWidth: 3, lineColor: CONDITIONAL_CHART_COLORS.outOfAccordance, fillColor: CONDITIONAL_CHART_COLORS.outOfAccordance }

        return { enabled: true, symbol: 'circle', radius: 5, lineWidth: 0, fillColor: CONDITIONAL_CHART_COLORS.inAccordance }
    }

    /** Linhas do tooltip de um ponto (sem a data, que o componente formata). */
    static tooltipLines(serieName: string, y: number, custom?: ConditionalPointCustomTP): string[] {

        const lines = [`<b>${serieName}</b>`, `Valor: ${RegulationConditionUtils.formatNumber(y)}`]

        if (custom?.kind !== 'parameter')
            return lines

        lines.push(custom.limit === null || custom.limit === undefined
            ? 'Sem limite aplicável'
            : `Limite aplicado: ${RegulationConditionUtils.formatNumber(custom.limit)}`)

        if (custom.referenceValue !== null && custom.referenceValue !== undefined)
            lines.push(`${custom.referenceName}: ${RegulationConditionUtils.formatNumber(custom.referenceValue)}`)

        return lines
    }
}
